import React, { useState } from 'react';
import { formatLicenseExceptions } from '../../utils/formatLicenseExceptions';
import JurisdictionDispositionBadge from './JurisdictionDispositionBadge';
import { NotAssessedSummary } from './AssessmentStatusBadge';
import ScopedReviewSummary from './ScopedReviewSummary';
import { scopedReviewSummary } from '../../utils/scopedReviewCaveat';
import { notAssessedPhases } from '../../utils/exportAssessment';
import { scheduleBCodeRows } from 'utils/scheduleBCodeResult';
interface AuditType {
  name?: string;
  hts_code?: string;
  cmdt_desc?: string;
  explanatory_notes?: string;
  section_chapter_notes?: string;
  cross_data_links?: string[];
  isExpands?: boolean;
  saveBtn?: any;
  selectedStep?: any;
  handleRowClick?: any;
  workflowData?: any;
}

const ExportResultsComp: React.FC<AuditType> = ({
  name,
  hts_code,
  cmdt_desc,
  isExpands,
  selectedStep,
  handleRowClick,
  workflowData
}) => {

  const [ isExpand, setIsExpand ] = useState(isExpands || false);
  const [ isExpandProd, setIsExpandProd ] = useState(true);
  const [ isExpandSecNotes, setIsExpandSecNotes ] = useState(true);
  const [ isExpandEEI, setIsExpandEEI ] = useState(true);
  const [ isExpandCross, setIsExpandCross ] = useState(true);
  const [ isExpandITAR, setIsExpandITAR ] = useState(true);
  const [ isExpandEAR, setIsExpandEAR ] = useState(true);
  const [ isExpandPga, setIsExpandPga ] = useState(true);

  const country =
    workflowData?.country_screening;

  const scheduleB =
    workflowData?.schedule_b;

  const exportScreening = workflowData?.export_screening;

  const eei = workflowData?.eei;

  const profile = workflowData?.profile;

  const nrc = workflowData?.nrc;

  const itar = workflowData?.itar;

  const earCcl = workflowData?.ear_ccl;

  // v2 conditional-routing (EXPORT_JURISDICTION_ROUTING_V2) — the PACKAGE-level jurisdiction
  // disposition (stopped / continued_multi_regime / continued_unresolved / clean) + reasons.
  // Top-level, like `pga`/`eei`; the whole block self-hides when absent (flag OFF / older ticket).
  const jurisdictionRouting = workflowData?.jurisdiction_routing;
  // Phases whose automated assessment could not be completed (resilience plan Slice 6).
  const hasNotAssessed = notAssessedPhases(workflowData).length > 0;
  // Upfront scoped review (scope-selection Slice 5): the user ran only some checks.
  const isScopedReview = Boolean(scopedReviewSummary(workflowData));

  // "Where to file" / PGA agency-routing summary (top-level, like `eei`). Advisory only —
  // the whole block self-hides when the routing is absent.
  const pga = workflowData?.pga_routing;
  const pgaAgencies = Array.isArray(pga?.agencies) ? pga.agencies : [];
  const pgaNotAssessed = Array.isArray(pga?.not_assessed) ? pga.not_assessed : [];
  const pgaNotRequested = Array.isArray(pga?.not_requested) ? pga.not_requested : [];
  const pgaAgencyLabel = (a?: any) => {
    const name = a?.agency || a?.agency_display || 'Agency';
    const code = a?.aes_license_type_code;
    return code ? `${name} (AES ${code})` : name;
  };
  // Appendix X niche-PGA requirements (Other agency requirements by commodity code). Read from the
  // control-list part (export_screening node) of the Country and Control List Check, falling back
  // to the old pga_routing location for tickets saved before the move.
  const esNiche = Array.isArray(exportScreening?.additional_pga_requirements)
    ? exportScreening.additional_pga_requirements
    : Array.isArray(pga?.additional_pga_requirements)
      ? pga.additional_pga_requirements
      : [];
  const esNicheCaveat =
    exportScreening?.additional_pga_caveat ?? pga?.additional_pga_caveat;
  const esNicheStatus = exportScreening?.additional_pga_status;
  const pgaNicheLabel = (x?: any) => {
    const agency = x?.pga_agency;
    const label = x?.agency_label;
    return agency && label ? `${agency} — ${label}` : label || agency || 'Agency';
  };
  const pgaNicheValue = (x?: any) => {
    const verify = x?.advisory
      ? 'code is not unique to this agency — verify whether a permit/notification applies'
      : 'this commodity code carries this agency’s export requirement';
    return [x?.program, x?.permit_forms, x?.description, verify]
      .filter((v) => v && String(v).trim())
      .join(' — ');
  };

  // ---- Concise transparency sub-rows for the final card (Slice 2+). Matches the card's
  // sub-row style — label indented 16px, value 28px — so the final card explains HOW each
  // determination was reached (why / ruled-out alternative / assumptions) without the full
  // detail that lives in the per-phase cards, saved work, and PDF. Renders nothing when empty.
  const capWord = (s?: string) =>
    s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
  const joinAlt = (a?: string, b?: string) => [a, b].filter(Boolean).join(' — ');
  const joinList = (items?: any[]) =>
    (Array.isArray(items) ? items.filter((x) => x && String(x).trim()) : []).join('; ');
  const renderFinalSubRow = (label: string, value?: React.ReactNode) => {
    if (
      value === undefined ||
      value === null ||
      (typeof value === 'string' && value.trim() === '')
    ) {
      return null;
    }
    return (
      <div className='classify-res-comp-sec-col'>
        <div className='classify-res-comp-sec'>
          <div className='classify-res-comp-sec-left'></div>
          <div
            className='classify-res-comp-sec-txt'
            style={{ paddingLeft: '16px' }}
          >
            {label}
          </div>
        </div>
        <div>
          <h2
            style={{
              color: 'var(--Text-Main, var(--text-primary))',
              fontSize: '14px',
              fontWeight: '400',
              paddingLeft: '28px',
              lineHeight: '1.5',
            }}
          >
            {value}
          </h2>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className={`classify-res-comp ${selectedStep === 1 ? 'selected' : ''}`} onClick={() => handleRowClick()}>
        <div className='classify-res-comp-header'>
          <div className='classify-res-comp-header-left-icon'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='16'
              height='16'
              viewBox='0 0 16 16'
              fill='none'
            >
              <g clipPath='url(#clip0_1_34180)'>
                <path
                  d='M5.3335 5.33325H5.34016M1.3335 3.46658L1.3335 6.44959C1.3335 6.77572 1.3335 6.93878 1.37034 7.09223C1.403 7.22827 1.45687 7.35833 1.52998 7.47763C1.61243 7.61219 1.72773 7.72749 1.95833 7.95809L7.07075 13.0705C7.86279 13.8625 8.2588 14.2586 8.71546 14.4069C9.11715 14.5375 9.54984 14.5375 9.95153 14.4069C10.4082 14.2586 10.8042 13.8625 11.5962 13.0705L13.0708 11.596C13.8628 10.804 14.2588 10.4079 14.4072 9.95128C14.5377 9.5496 14.5377 9.1169 14.4072 8.71522C14.2588 8.25856 13.8628 7.86254 13.0708 7.07051L7.95833 1.95809C7.72773 1.72749 7.61243 1.61219 7.47787 1.52973C7.35858 1.45663 7.22852 1.40275 7.09247 1.37009C6.93902 1.33325 6.77596 1.33325 6.44984 1.33325L3.46683 1.33325C2.72009 1.33325 2.34672 1.33325 2.06151 1.47858C1.81063 1.60641 1.60665 1.81038 1.47882 2.06126C1.3335 2.34648 1.3335 2.71985 1.3335 3.46658ZM5.66683 5.33325C5.66683 5.51735 5.51759 5.66658 5.3335 5.66658C5.1494 5.66658 5.00016 5.51735 5.00016 5.33325C5.00016 5.14916 5.1494 4.99992 5.3335 4.99992C5.51759 4.99992 5.66683 5.14916 5.66683 5.33325Z'
                  stroke='var(--text-classify)'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </g>
              <defs>
                <clipPath id='clip0_1_34180'>
                  <rect width='16' height='16' fill='white' />
                </clipPath>
              </defs>
            </svg>
          </div>
          <div className='classify-res-comp-header-text'>Summary Results</div>
          <div
            className='classify-res-comp-header-left-icon'
            style={{ paddingTop: '2px' }}
          >
            {cmdt_desc !== '' ? (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
              >
                <path
                  d='M13.3327 4L5.99935 11.3333L2.66602 8'
                  stroke='#17B26A'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            ) : (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
              >
                <g clipPath='url(#clip0_1_34184)'>
                  <path
                    d='M8.00016 3.99992V7.99992L10.6668 9.33325M14.6668 7.99992C14.6668 11.6818 11.6821 14.6666 8.00016 14.6666C4.31826 14.6666 1.3335 11.6818 1.3335 7.99992C1.3335 4.31802 4.31826 1.33325 8.00016 1.33325C11.6821 1.33325 14.6668 4.31802 14.6668 7.99992Z'
                    stroke='#F79009'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </g>
                <defs>
                  <clipPath id='clip0_1_34184'>
                    <rect width='16' height='16' fill='white' />
                  </clipPath>
                </defs>
              </svg>
            )}
          </div>
          <div
            className='classify-res-comp-header-left-icon'
            style={{ paddingTop: '2px' }}
          >
            {!isExpand ? (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
                onClick={() => setIsExpand(!isExpand)}
              >
                <path
                  d='M10 12L6 8L10 4'
                  stroke='var(--text-classify)'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            ) : (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
                onClick={() => setIsExpand(!isExpand)}
              >
                <path
                  d='M4 6L8 10L12 6'
                  stroke='var(--text-classify)'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            )}
          </div>
        </div>

        {!isExpand ? (
          <>
            <p className='classification-status'>
              {cmdt_desc ? cmdt_desc : 'Awaiting information...'}
            </p>
            {hasNotAssessed && (
              <div style={{ paddingTop: '8px' }}>
                <NotAssessedSummary data={workflowData} variant='pill' />
              </div>
            )}
            {isScopedReview && (
              <div style={{ paddingTop: '8px' }}>
                <ScopedReviewSummary data={workflowData} variant='pill' />
              </div>
            )}
          </>
        ) : (
          <>
            {/* Manual review required — any phase whose automated assessment could not be
                completed. Shown FIRST: no overall clearance may be read from the result
                below while it stands. Self-hides when every phase was assessed. */}
            {hasNotAssessed && (
              <>
                <div className='classify-res-comp-sec-col'>
                  <div className='classify-res-comp-sec'>
                    <div className='classify-res-comp-sec-txt'>Manual Review Required</div>
                  </div>
                  <div style={{ paddingTop: '6px' }}>
                    <NotAssessedSummary data={workflowData} />
                  </div>
                </div>

                <div className='export-divider' style={{ display: 'flex' }} />
              </>
            )}

            {/* Scoped Review — the user ran only the checks picked in the upfront scope popup.
                Directly under Manual Review so a partial run never reads as a full clearance.
                Self-hides on a full review. */}
            {isScopedReview && (
              <>
                <div className='classify-res-comp-sec-col'>
                  <div className='classify-res-comp-sec'>
                    <div className='classify-res-comp-sec-txt'>Scoped Review</div>
                  </div>
                  <div style={{ paddingTop: '6px' }}>
                    <ScopedReviewSummary data={workflowData} />
                  </div>
                </div>

                <div className='export-divider' style={{ display: 'flex' }} />
              </>
            )}

            {/* Where to File — PGA agency-routing summary (advisory). Self-hides when the
                routing is absent (e.g. an older saved ticket predating this feature). */}
            {pga && (
              <>
                <div className='classify-res-comp-sec-col'>
                  <div className='classify-res-comp-sec'>
                    <div className='classify-res-comp-sec-left'>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='16'
                        height='16'
                        viewBox='0 0 16 16'
                        fill='none'
                      >
                        <path
                          d='M14.6667 1.33333L7.33333 8.66667M14.6667 1.33333L10 14.6667L7.33333 8.66667L1.33333 6L14.6667 1.33333Z'
                          stroke='var(--text-classify)'
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    </div>
                    <div className='classify-res-comp-sec-txt'>Where to File</div>
                    <div className='classify-res-comp-sec-left'>
                      {!isExpandPga ? (
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          width='16'
                          height='16'
                          viewBox='0 0 16 16'
                          fill='none'
                          onClick={() => setIsExpandPga(!isExpandPga)}
                        >
                          <path
                            d='M10 12L6 8L10 4'
                            stroke='var(--text-classify)'
                            strokeWidth='1.5'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          />
                        </svg>
                      ) : (
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          width='16'
                          height='16'
                          viewBox='0 0 16 16'
                          fill='none'
                          onClick={() => setIsExpandPga(!isExpandPga)}
                        >
                          <path
                            d='M4 6L8 10L12 6'
                            stroke='var(--text-classify)'
                            strokeWidth='1.5'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          />
                        </svg>
                      )}
                    </div>
                  </div>
                  {isExpandPga && (
                    <>
                      {renderFinalSubRow('Determination', pga?.headline)}
                      {renderFinalSubRow('Summary', pga?.prose)}
                      {pgaAgencies.map((a: any, i: number) => (
                        <React.Fragment key={i}>
                          {renderFinalSubRow(pgaAgencyLabel(a), a?.next_step)}
                        </React.Fragment>
                      ))}
                      {pgaNotAssessed.length > 0 &&
                        renderFinalSubRow('Not assessed', pgaNotAssessed.join(' '))}
                      {pgaNotRequested.length > 0 &&
                        renderFinalSubRow(
                          'Not requested (scoped review)',
                          `${pgaNotRequested.join('; ')} — not run, so treat as open, not cleared.`,
                        )}
                      {/* Appendix X niche-PGA requirements live in the Country and Control
                          List Check section below (its control-list part). */}
                      {renderFinalSubRow('Note', pga?.caveat)}
                    </>
                  )}
                </div>

                <div className='export-divider' style={{ display: 'flex' }} />
              </>
            )}

            <div className='classify-res-comp-body'>
              <div className='classify-res-comp-body-cont'>
                <div className='classify-res-comp-body-cont-sec'>
                  <div className='classify-res-comp-body-cont-sec-txt'>
                    {name}
                  </div>
                  {/* <div className='classify-res-comp-body-cont-sec-icon'>
                    <div className='classify-res-comp-body-cont-sec-icon-txt'>
                      Medium Confidence
                    </div>
                  </div> */}
                </div>
                <div className='classify-res-comp-body-cont-txt'>
                  { scheduleB?.not_applicable
                    ? (scheduleB?.not_applicable_reason || 'Not applicable')
                    : (scheduleB?.schedule_b_code || 'N/A') }
                </div>
              </div>
            </div>

            {/* Slice B: how the user's own code was checked (self-hiding when classified). */}
            {!scheduleB?.not_applicable &&
              renderFinalSubRow('Export code', scheduleBCodeRows(scheduleB).exportCode)}
            {!scheduleB?.not_applicable &&
              renderFinalSubRow('AES export check', scheduleBCodeRows(scheduleB).aesCheck)}
            {!scheduleB?.not_applicable &&
              renderFinalSubRow(
                'Where the code came from',
                scheduleBCodeRows(scheduleB).codeSource,
              )}
            {/* Schedule B transparency on the final card (parity with NRC/ITAR/CCL). */}
            {!scheduleB?.not_applicable &&
              renderFinalSubRow('Why', scheduleB?.selection_rationale)}
            {!scheduleB?.not_applicable &&
              renderFinalSubRow(
                'Ruled out',
                joinAlt(
                  scheduleB?.closest_alternative_hs_code,
                  scheduleB?.closest_alternative_reason
                )
              )}

            <div className='classify-res-comp-sec-col'>
              <div className='classify-res-comp-sec'>
                <div className='classify-res-comp-sec-left'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='16'
                    height='16'
                    viewBox='0 0 16 16'
                    fill='none'
                  >
                    <path
                      d='M13.6666 4.85173L7.99998 7.99987M7.99998 7.99987L2.33331 4.85173M7.99998 7.99987L8 14.3332M14 10.7056V5.29419C14 5.06576 14 4.95155 13.9663 4.84968C13.9366 4.75956 13.8879 4.67684 13.8236 4.60705C13.7509 4.52816 13.651 4.47269 13.4514 4.36176L8.51802 1.62102C8.32895 1.51598 8.23442 1.46346 8.1343 1.44287C8.0457 1.42464 7.95431 1.42464 7.8657 1.44287C7.76559 1.46346 7.67105 1.51598 7.48198 1.62102L2.54865 4.36176C2.34896 4.47269 2.24912 4.52816 2.17642 4.60705C2.11211 4.67684 2.06343 4.75957 2.03366 4.84968C2 4.95155 2 5.06576 2 5.29419V10.7056C2 10.934 2 11.0482 2.03366 11.1501C2.06343 11.2402 2.11211 11.3229 2.17642 11.3927C2.24912 11.4716 2.34897 11.5271 2.54865 11.638L7.48198 14.3788C7.67105 14.4838 7.76559 14.5363 7.8657 14.5569C7.95431 14.5751 8.0457 14.5751 8.1343 14.5569C8.23442 14.5363 8.32895 14.4838 8.51802 14.3788L13.4514 11.638C13.651 11.5271 13.7509 11.4716 13.8236 11.3927C13.8879 11.3229 13.9366 11.2402 13.9663 11.1501C14 11.0482 14 10.934 14 10.7056Z'
                      stroke='var(--text-classify)'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                    <path
                      d='M11 6.33333L5 3'
                      stroke='var(--text-classify)'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </div>
                <div className='classify-res-comp-sec-txt'>
                  Product Information
                </div>
                <div className='classify-res-comp-sec-left'>
                  {!isExpandProd ? (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                      onClick={() => setIsExpandProd(!isExpandProd)}
                    >
                      <path
                        d='M10 12L6 8L10 4'
                        stroke='var(--text-classify)'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                      onClick={() => setIsExpandProd(!isExpandProd)}
                    >
                      <path
                        d='M4 6L8 10L12 6'
                        stroke='var(--text-classify)'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  )}
                </div>
              </div>
              {isExpandProd && (
                <div>
                  {cmdt_desc && (
                    <h2
                      style={{
                        color: 'var(--Text-Main, var(--text-primary))',
                        fontSize: '14px',
                        fontWeight: '400',
                        paddingLeft: '28px',
                      }}
                    >
                      {cmdt_desc}
                    </h2>
                  )}
                </div>
              )}
            </div>

            <div className='export-divider' style={{ display: 'flex' }} />

            <div className='classify-res-comp-sec-col'>
              <div className='classify-res-comp-sec'>
                <div className='classify-res-comp-sec-left'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='15'
                    height='13'
                    viewBox='0 0 15 13'
                    fill='none'
                  >
                    <path
                      d='M7.41667 11.4167H2.88333C2.1366 11.4167 1.76323 11.4167 1.47801 11.2713C1.22713 11.1435 1.02316 10.9395 0.895325 10.6887C0.75 10.4034 0.75 10.0301 0.75 9.28333V2.88333C0.75 2.1366 0.75 1.76323 0.895325 1.47801C1.02316 1.22713 1.22713 1.02316 1.47801 0.895325C1.76323 0.75 2.1366 0.75 2.88333 0.75H3.15C4.64347 0.75 5.39021 0.75 5.96064 1.04065C6.46241 1.29631 6.87035 1.70426 7.12602 2.20603C7.41667 2.77646 7.41667 3.52319 7.41667 5.01667M7.41667 11.4167V5.01667M7.41667 11.4167H11.95C12.6967 11.4167 13.0701 11.4167 13.3553 11.2713C13.6062 11.1435 13.8102 10.9395 13.938 10.6887C14.0833 10.4034 14.0833 10.0301 14.0833 9.28333V2.88333C14.0833 2.1366 14.0833 1.76323 13.938 1.47801C13.8102 1.22713 13.6062 1.02316 13.3553 0.895325C13.0701 0.75 12.6967 0.75 11.95 0.75H11.6833C10.1899 0.75 9.44312 0.75 8.87269 1.04065C8.37093 1.29631 7.96298 1.70426 7.70732 2.20603C7.41667 2.77646 7.41667 3.52319 7.41667 5.01667'
                      stroke='var(--text-classify)'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </div>
                <div className='classify-res-comp-sec-txt'>
                  Country and Control List Check
                </div>
                <div className='classify-res-comp-sec-left'>
                  {!isExpandSecNotes ? (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                      onClick={() => setIsExpandSecNotes(!isExpandSecNotes)}
                    >
                      <path
                        d='M10 12L6 8L10 4'
                        stroke='var(--text-classify)'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                      onClick={() => setIsExpandSecNotes(!isExpandSecNotes)}
                    >
                      <path
                        d='M4 6L8 10L12 6'
                        stroke='var(--text-classify)'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  )}
                </div>
              </div>
              {isExpandSecNotes && (
                <>
                  <div className='classify-res-comp-sec-col'>
                    <div className='classify-res-comp-sec'>
                      <div className='classify-res-comp-sec-left'>
                        
                      </div>
                      <div className='classify-res-comp-sec-txt' style={{ paddingLeft: '16px' }}>
                        Destination Country
                      </div>
                    </div>
                    <div>
                      <h2
                        style={{
                          color: 'var(--Text-Main, var(--text-primary))',
                          fontSize: '14px',
                          fontWeight: '400',
                          paddingLeft: '28px',
                        }}
                      >
                        {country?.normalized_country_name || 'N/A'}
                      </h2>
                    </div>
                  </div>
            
                  <div className='classify-res-comp-sec-col'>
                      <div className='classify-res-comp-sec'>
                        <div className='classify-res-comp-sec-left'>
                          
                        </div>
                        <div className='classify-res-comp-sec-txt' style={{ paddingLeft: '16px' }}>
                          Any Country Risk Hit
                        </div>
                      </div>
                      <div>
                        <h2
                          style={{
                            color: 'var(--Text-Main, var(--text-primary))',
                            fontSize: '14px',
                            fontWeight: '400',
                            paddingLeft: '28px',
                          }}
                        >
                          {country?.has_any_country_risk_hit || 'N/A'}
                        </h2>
                      </div>
                    </div>
                {/* Control-list part (the pre-merge "Screening & Compliance" rows). */}
                <div className='classify-res-comp-sec-col'>
                  <div className='classify-res-comp-sec'>
                    <div className='classify-res-comp-sec-left'>
                      
                    </div>
                    <div className='classify-res-comp-sec-txt' style={{ paddingLeft: '16px' }}>
                      Control List Risk Level
                    </div>
                  </div>
                  <div>
                    <h2
                      style={{
                        color: 'var(--Text-Main, var(--text-primary))',
                        fontSize: '14px',
                        fontWeight: '400',
                        paddingLeft: '28px',
                      }}
                    >
                      {exportScreening?.not_applicable
                        ? (exportScreening?.not_applicable_reason || 'Not applicable')
                        : (exportScreening?.risk_level || 'N/A')}
                    </h2>
                  </div>
                </div>

                {/* Appendix X — niche PGA requirements by commodity code (AMS/ATF/DEA/EPA/FWS/NMFS).
                    Part of the control-list check; surfaced here and on its phase card.
                    Rows when present; a short note when no commodity code was classified; self-hides
                    when a code maps to no niche requirement. (Replaces the dead "Regulatory Burden"
                    field, which the export_screening phase never populates.) */}
                {esNiche.length > 0 ? (
                  <>
                    {renderFinalSubRow(
                      'Other agency requirements (by commodity code)',
                      esNicheCaveat,
                    )}
                    {esNiche.map((x: any, i: number) => (
                      <React.Fragment key={`es-pga-x-${i}`}>
                        {renderFinalSubRow(pgaNicheLabel(x), pgaNicheValue(x))}
                      </React.Fragment>
                    ))}
                  </>
                ) : esNicheStatus === 'no_code' ? (
                  renderFinalSubRow(
                    'Other agency requirements (by commodity code)',
                    'Commodity-code PGA requirements (Appendix X) could not be determined — no Schedule B/HTS code was classified.',
                  )
                ) : null}
                </>
              )}
            </div>

            <div className='export-divider' style={{ display: 'flex' }} />

            <div className='classify-res-comp-sec-col'>
              <div className='classify-res-comp-sec'>
                <div className='classify-res-comp-sec-left'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='16'
                    height='16'
                    viewBox='0 0 16 16'
                    fill='none'
                  >
                    <path
                      d='M9.33333 1.51314V4.26667C9.33333 4.6400 9.33333 4.82670 9.40599 4.96933C9.4699 5.09477 9.57189 5.19675 9.69733 5.26067C9.83994 5.33333 10.0266 5.33333 10.4 5.33333H13.1535M9.33333 11.3333H5.33333M10.6667 8.66667H5.33333M13.3333 6.65898V11.4667C13.3333 12.5868 13.3333 13.1468 13.1153 13.5746C12.9236 13.951 12.6176 14.2569 12.2413 14.4487C11.8135 14.6667 11.2534 14.6667 10.1333 14.6667H5.86667C4.74656 14.6667 4.18651 14.6667 3.75869 14.4487C3.38236 14.2569 3.0764 13.951 2.88465 13.5746C2.66667 13.1468 2.66667 12.5868 2.66667 11.4667V4.53333C2.66667 3.41323 2.66667 2.85317 2.88465 2.42535C3.0764 2.04903 3.38236 1.74307 3.75869 1.55132C4.18651 1.33333 4.74656 1.33333 5.86667 1.33333H8.00768C8.49574 1.33333 8.73977 1.33333 8.96942 1.38847C9.17301 1.43735 9.36764 1.51802 9.54614 1.62761C9.74749 1.75124 9.92004 1.92379 10.2651 2.26889L12.3978 4.40156C12.7429 4.74666 12.9155 4.91922 13.0391 5.12057C13.1487 5.29907 13.2293 5.4937 13.2782 5.69729C13.3333 5.92694 13.3333 6.17097 13.3333 6.65898Z'
                      stroke='var(--text-classify)'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </div>
                <div className='classify-res-comp-sec-txt'>
                  Product Profile & EEI Check
                </div>
                <div className='classify-res-comp-sec-left'>
                  {!isExpandEEI ? (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                      onClick={() => setIsExpandEEI(!isExpandEEI)}
                    >
                      <path
                        d='M10 12L6 8L10 4'
                        stroke='var(--text-classify)'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                      onClick={() => setIsExpandEEI(!isExpandEEI)}
                    >
                      <path
                        d='M4 6L8 10L12 6'
                        stroke='var(--text-classify)'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  )}
                </div>
              </div>
              {isExpandEEI && (
                <>
                  {profile ? (
                    <>
                      <div className='classify-res-comp-sec-col'>
                        <div className='classify-res-comp-sec'>
                          <div className='classify-res-comp-sec-left'></div>
                          <div className='classify-res-comp-sec-txt' style={{ paddingLeft: '16px' }}>
                            Export Type
                          </div>
                        </div>
                        <div>
                          <h2
                            style={{
                              color: 'var(--Text-Main, var(--text-primary))',
                              fontSize: '14px',
                              fontWeight: '400',
                              paddingLeft: '28px',
                            }}
                          >
                            {profile?.export_type_display || 'N/A'}
                          </h2>
                        </div>
                      </div>

                      <div className='classify-res-comp-sec-col'>
                        <div className='classify-res-comp-sec'>
                          <div className='classify-res-comp-sec-left'></div>
                          <div className='classify-res-comp-sec-txt' style={{ paddingLeft: '16px' }}>
                            Tangibility
                          </div>
                        </div>
                        <div>
                          <h2
                            style={{
                              color: 'var(--Text-Main, var(--text-primary))',
                              fontSize: '14px',
                              fontWeight: '400',
                              paddingLeft: '28px',
                            }}
                          >
                            {profile?.tangible_display || 'N/A'}
                          </h2>
                        </div>
                      </div>

                      <div className='classify-res-comp-sec-col'>
                        <div className='classify-res-comp-sec'>
                          <div className='classify-res-comp-sec-left'></div>
                          <div className='classify-res-comp-sec-txt' style={{ paddingLeft: '16px' }}>
                            Destination Country
                          </div>
                        </div>
                        <div>
                          <h2
                            style={{
                              color: 'var(--Text-Main, var(--text-primary))',
                              fontSize: '14px',
                              fontWeight: '400',
                              paddingLeft: '28px',
                            }}
                          >
                            {profile?.destination_country || 'N/A'}
                          </h2>
                        </div>
                      </div>
                    </>
                  ) : null}

                  <div className='classify-res-comp-sec-col'>
                    <div className='classify-res-comp-sec'>
                      <div className='classify-res-comp-sec-left'></div>
                      <div className='classify-res-comp-sec-txt' style={{ paddingLeft: '16px' }}>
                        Filing Determination
                      </div>
                    </div>
                    <div>
                      <h2
                        style={{
                          color: 'var(--Text-Main, var(--text-primary))',
                          fontSize: '14px',
                          fontWeight: '400',
                          paddingLeft: '28px',
                        }}
                      >
                        {eei?.filing_determination || 'N/A'}
                      </h2>
                    </div>
                  </div>

                  <div className='classify-res-comp-sec-col'>
                    <div className='classify-res-comp-sec'>
                      <div className='classify-res-comp-sec-left'></div>
                      <div className='classify-res-comp-sec-txt' style={{ paddingLeft: '16px' }}>
                        Regulatory Citation
                      </div>
                    </div>
                    <div>
                      <h2
                        style={{
                          color: 'var(--Text-Main, var(--text-primary))',
                          fontSize: '14px',
                          fontWeight: '400',
                          paddingLeft: '28px',
                        }}
                      >
                        {eei?.citation || 'N/A'}
                      </h2>
                    </div>
                  </div>

                  {eei?.aes_exemption_legend ? (
                    <div className='classify-res-comp-sec-col'>
                      <div className='classify-res-comp-sec'>
                        <div className='classify-res-comp-sec-left'></div>
                        <div className='classify-res-comp-sec-txt' style={{ paddingLeft: '16px' }}>
                          AES Exemption Legend
                        </div>
                      </div>
                      <div>
                        <h2
                          style={{
                            color: 'var(--Text-Main, var(--text-primary))',
                            fontSize: '14px',
                            fontWeight: '400',
                            paddingLeft: '28px',
                          }}
                        >
                          {eei.aes_exemption_legend}
                        </h2>
                      </div>
                    </div>
                  ) : null}
                </>
              )}
            </div>

            <div className='export-divider' style={{ display: 'flex' }} />

            {/* Jurisdiction Routing — the v2 PACKAGE-level disposition (stopped / continued /
                unresolved) + reasons + a prominent manual-review caution when unresolved.
                Placed directly above Nuclear (NRC + DOE), where the jurisdiction checks begin;
                Where to File stays the top "what to do next" output. Self-hides when absent
                (flag OFF / older saved ticket). */}
            {jurisdictionRouting && (
              <>
                <div className='classify-res-comp-sec-col'>
                  <div className='classify-res-comp-sec'>
                    <div className='classify-res-comp-sec-left'>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='16'
                        height='16'
                        viewBox='0 0 16 16'
                        fill='none'
                      >
                        <path
                          d='M8 1.33333L2.66667 3.33333V7.33333C2.66667 10.6667 4.93333 12.9333 8 14.6667C11.0667 12.9333 13.3333 10.6667 13.3333 7.33333V3.33333L8 1.33333Z'
                          stroke='var(--text-classify)'
                          strokeWidth='1.5'
                          strokeLinejoin='round'
                        />
                        <path
                          d='M5.83333 8L7.33333 9.5L10.5 6.33333'
                          stroke='var(--text-classify)'
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    </div>
                    <div className='classify-res-comp-sec-txt'>Jurisdiction Routing</div>
                  </div>
                  <div style={{ paddingLeft: '28px', paddingTop: '6px' }}>
                    <JurisdictionDispositionBadge routing={jurisdictionRouting} />
                  </div>
                </div>

                <div className='export-divider' style={{ display: 'flex' }} />
              </>
            )}

            <div className='classify-res-comp-sec-col'>
              <div className='classify-res-comp-sec'>
                <div className='classify-res-comp-sec-left'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='16'
                    height='16'
                    viewBox='0 0 16 16'
                    fill='none'
                  >
                    <path
                      d='M2 14H14M4 12V6.66665M6.66667 12V6.66665M9.33333 12V6.66665M12 12V6.66665M13.3333 4.66665L8.28267 1.50998C8.18012 1.44589 8.12885 1.41385 8.07386 1.40135C8.02524 1.3903 7.97476 1.3903 7.92614 1.40135C7.87115 1.41385 7.81988 1.44589 7.71733 1.50998L2.66667 4.66665H13.3333Z'
                      stroke='var(--text-classify)'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </div>
                <div className='classify-res-comp-sec-txt'>Nuclear (NRC + DOE)</div>
                <div className='classify-res-comp-sec-left'>
                  {!isExpandCross ? (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                      onClick={() => setIsExpandCross(!isExpandCross)}
                    >
                      <path
                        d='M10 12L6 8L10 4'
                        stroke='var(--text-classify)'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                      onClick={() => setIsExpandCross(!isExpandCross)}
                    >
                      <path
                        d='M4 6L8 10L12 6'
                        stroke='var(--text-classify)'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  )}
                </div>
              </div>
              {isExpandCross && (
                <>
                  <div className='classify-res-comp-sec-col'>
                    <div className='classify-res-comp-sec'>
                      <div className='classify-res-comp-sec-left'>
                        
                      </div>
                      <div className='classify-res-comp-sec-txt' style={{ paddingLeft: '16px' }}>
                        Controlled
                      </div>
                    </div>
                    <div>
                      <h2
                        style={{
                          color: 'var(--Text-Main, var(--text-primary))',
                          fontSize: '14px',
                          fontWeight: '400',
                          paddingLeft: '28px',
                        }}
                      >
                        {nrc?.controlled || 'N/A'}
                      </h2>
                    </div>
                  </div>
                  {renderFinalSubRow('Confidence', capWord(nrc?.confidence))}
                  {renderFinalSubRow('Why', nrc?.reason)}
                  {renderFinalSubRow(
                    'Ruled out',
                    joinAlt(nrc?.closest_alternative, nrc?.closest_alternative_reason)
                  )}
                  {renderFinalSubRow('Assumptions', joinList(nrc?.assumptions))}
                  {nrc?.doe_controlled && (
                    <div className='classify-res-comp-sec-col'>
                      <div className='classify-res-comp-sec'>
                        <div className='classify-res-comp-sec-left'></div>
                        <div className='classify-res-comp-sec-txt' style={{ paddingLeft: '16px' }}>
                          DOE Part 810
                        </div>
                      </div>
                      <div>
                        <h2
                          style={{
                            color: 'var(--Text-Main, var(--text-primary))',
                            fontSize: '14px',
                            fontWeight: '400',
                            paddingLeft: '28px',
                          }}
                        >
                          {nrc?.doe_controlled || 'N/A'}
                        </h2>
                      </div>
                    </div>
                  )}
                  {nrc?.doe_controlled && (
                    <>
                      {renderFinalSubRow(
                        'DOE Confidence',
                        capWord(nrc?.doe_confidence)
                      )}
                      {renderFinalSubRow('DOE Why', nrc?.doe_reason)}
                      {renderFinalSubRow(
                        'DOE Ruled out',
                        joinAlt(
                          nrc?.doe_closest_alternative,
                          nrc?.doe_closest_alternative_reason
                        )
                      )}
                      {renderFinalSubRow(
                        'DOE Assumptions',
                        joinList(nrc?.doe_assumptions)
                      )}
                    </>
                  )}
                </>
              )}
            </div>

            <div className='export-divider' style={{ display: 'flex' }} />

            <div className='classify-res-comp-sec-col'>
              <div className='classify-res-comp-sec'>
                <div className='classify-res-comp-sec-left'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='16'
                    height='16'
                    viewBox='0 0 16 16'
                    fill='none'
                  >
                    <path
                      d='M2 14H14M4 12V6.66665M6.66667 12V6.66665M9.33333 12V6.66665M12 12V6.66665M13.3333 4.66665L8.28267 1.50998C8.18012 1.44589 8.12885 1.41385 8.07386 1.40135C8.02524 1.3903 7.97476 1.3903 7.92614 1.40135C7.87115 1.41385 7.81988 1.44589 7.71733 1.50998L2.66667 4.66665H13.3333Z'
                      stroke='var(--text-classify)'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </div>
                <div className='classify-res-comp-sec-txt'>ITAR Check</div>
                <div className='classify-res-comp-sec-left'>
                  {!isExpandITAR ? (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                      onClick={() => setIsExpandITAR(!isExpandITAR)}
                    >
                      <path
                        d='M10 12L6 8L10 4'
                        stroke='var(--text-classify)'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                      onClick={() => setIsExpandITAR(!isExpandITAR)}
                    >
                      <path
                        d='M4 6L8 10L12 6'
                        stroke='var(--text-classify)'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  )}
                </div>
              </div>
              {isExpandITAR && (
                <>
                  <div className='classify-res-comp-sec-col'>
                    <div className='classify-res-comp-sec'>
                      <div className='classify-res-comp-sec-left'>
                        
                      </div>
                      <div className='classify-res-comp-sec-txt' style={{ paddingLeft: '16px' }}>
                        Controlled
                      </div>
                    </div>
                    <div>
                      <h2
                        style={{
                          color: 'var(--Text-Main, var(--text-primary))',
                          fontSize: '14px',
                          fontWeight: '400',
                          paddingLeft: '28px',
                        }}
                      >
                        {itar?.controlled || 'N/A'}
                      </h2>
                    </div>
                  </div>
                  {renderFinalSubRow('Confidence', capWord(itar?.confidence))}
                  {renderFinalSubRow('Why', itar?.reason)}
                  {renderFinalSubRow(
                    'Ruled out',
                    joinAlt(itar?.closest_alternative_category, itar?.closest_alternative_reason)
                  )}
                  {renderFinalSubRow('Assumptions', joinList(itar?.assumptions))}
                  {itar?.exemption_note && (
                    <div className='classify-res-comp-sec-col'>
                      <div className='classify-res-comp-sec'>
                        <div className='classify-res-comp-sec-left'></div>
                        <div className='classify-res-comp-sec-txt' style={{ paddingLeft: '16px' }}>
                          Exemption outlook
                        </div>
                      </div>
                      <div>
                        <h2
                          style={{
                            color: 'var(--Text-Main, var(--text-primary))',
                            fontSize: '14px',
                            fontWeight: '400',
                            paddingLeft: '28px',
                          }}
                        >
                          {itar.exemption_note}
                        </h2>
                      </div>
                    </div>
                  )}
                  {itar?.fms_channel === true && (
                    <div className='classify-res-comp-sec-col'>
                      <div className='classify-res-comp-sec'>
                        <div className='classify-res-comp-sec-left'></div>
                        <div className='classify-res-comp-sec-txt' style={{ paddingLeft: '16px' }}>
                          FMS channel (§126.6)
                        </div>
                      </div>
                      <div>
                        <h2
                          style={{
                            color: 'var(--Text-Main, var(--text-primary))',
                            fontSize: '14px',
                            fontWeight: '400',
                            paddingLeft: '28px',
                          }}
                        >
                          Foreign Military Sales — follows the DoD/DSCA Letter of Offer and
                          Acceptance rather than a standard DSP license.
                        </h2>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className='export-divider' style={{ display: 'flex' }} />

            <div className='classify-res-comp-sec-col'>
              <div className='classify-res-comp-sec'>
                <div className='classify-res-comp-sec-left'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='16'
                    height='16'
                    viewBox='0 0 16 16'
                    fill='none'
                  >
                    <path
                      d='M2 14H14M4 12V6.66665M6.66667 12V6.66665M9.33333 12V6.66665M12 12V6.66665M13.3333 4.66665L8.28267 1.50998C8.18012 1.44589 8.12885 1.41385 8.07386 1.40135C8.02524 1.3903 7.97476 1.3903 7.92614 1.40135C7.87115 1.41385 7.81988 1.44589 7.71733 1.50998L2.66667 4.66665H13.3333Z'
                      stroke='var(--text-classify)'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </div>
                <div className='classify-res-comp-sec-txt'>EAR / CCL Check</div>
                <div className='classify-res-comp-sec-left'>
                  {!isExpandEAR ? (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                      onClick={() => setIsExpandEAR(!isExpandEAR)}
                    >
                      <path
                        d='M10 12L6 8L10 4'
                        stroke='var(--text-classify)'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                      onClick={() => setIsExpandEAR(!isExpandEAR)}
                    >
                      <path
                        d='M4 6L8 10L12 6'
                        stroke='var(--text-classify)'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  )}
                </div>
              </div>
              {isExpandEAR && (
                <>
                  <div className='classify-res-comp-sec-col'>
                    <div className='classify-res-comp-sec'>
                      <div className='classify-res-comp-sec-left'>
                        
                      </div>
                      <div className='classify-res-comp-sec-txt' style={{ paddingLeft: '16px' }}>
                      Classification Result
                      </div>
                    </div>
                    <div>
                      <h2
                        style={{
                          color: 'var(--Text-Main, var(--text-primary))',
                          fontSize: '14px',
                          fontWeight: '400',
                          paddingLeft: '28px',
                        }}
                      >
                        {hts_code || 'N/A'}
                      </h2>
                    </div>
                  </div>
                  {renderFinalSubRow('Confidence', capWord(earCcl?.confidence))}
                  {renderFinalSubRow('Why', earCcl?.reasoning)}
                  {renderFinalSubRow(
                    'Ruled out',
                    joinAlt(
                      earCcl?.closest_alternative_eccn,
                      earCcl?.closest_alternative_reason
                    )
                  )}
                  {renderFinalSubRow('Assumptions', joinList(earCcl?.assumptions))}
                  {/* CHIPS §744.23 — advanced-computing / SME end-use heads-up; self-hides
                      (only advanced-computing / advanced-node-IC / SME items carry it). */}
                  {renderFinalSubRow(
                    'Advanced-computing (§744.23)',
                    earCcl?.advanced_computing_note
                  )}
                  {/* License Exception ENC (§740.17) — deterministic self-classification-report-vs-CCATS
                      pathway for Category 5 Part 2 encryption ECCNs. Self-hides on non-encryption items. */}
                  {renderFinalSubRow(
                    'License Exception ENC (§740.17)',
                    earCcl?.enc_pathway
                  )}
                  {/* License Exceptions (EAR Part 740) — deterministic grounded determination as its
                      OWN row (headline + candidate exceptions + reason none apply). pre-line keeps the
                      per-exception bullet lines on separate rows. Self-hides when empty. */}
                  {renderFinalSubRow(
                    'License Exceptions (15 CFR Part 740)',
                    (() => {
                      const lxText = formatLicenseExceptions(
                        earCcl?.license_exceptions,
                      );
                      return lxText ? (
                        <span style={{ whiteSpace: 'pre-line' }}>{lxText}</span>
                      ) : undefined;
                    })(),
                  )}
                  {/* Slice 4 — international / multilateral overlay (EU dual-use + originating
                      regimes). Supplementary screening signal; each row hides when empty. */}
                  {renderFinalSubRow(
                    'Multilateral / regime',
                    joinList(earCcl?.regime_basis)
                  )}
                  {renderFinalSubRow('EU dual-use', earCcl?.eu_dual_use_entry)}
                  {/* Task #6 — BIS guidance (US/BIS interpretive FAQs / advisories). Its OWN
                      self-hiding row, NOT folded into the international overlay. Supplementary
                      grounding — the CFR determines the outcome. */}
                  {renderFinalSubRow('BIS guidance', earCcl?.bis_guidance_notes)}
                  {/* Task #5 — deterministic foreign-control correlation (EU / UK / Canada). ONE row
                      per jurisdiction: an exact-number match, or a muted low-confidence "no direct
                      equivalent — verify" advisory (the deliberate not-1:1 surface). Self-hides. */}
                  {renderFinalSubRow(
                    'International equivalents (EU / UK / Canada)',
                    earCcl?.foreign_equivalents?.length ? (
                      <>
                        {earCcl.foreign_equivalents.map((fe: any, i: number) => (
                          <div
                            key={i}
                            style={
                              fe?.confidence === 'low'
                                ? { color: 'var(--text-tertiary)' }
                                : undefined
                            }
                          >
                            {fe?.jurisdiction}:{' '}
                            {fe?.confidence === 'low'
                              ? 'no direct equivalent — verify locally'
                              : `${fe?.control_number || ''}${
                                  fe?.regime ? ` (${fe.regime})` : ''
                                }`}
                          </div>
                        ))}
                        <div
                          style={{
                            fontSize: '12px',
                            color: 'var(--text-tertiary)',
                            marginTop: '4px',
                          }}
                        >
                          Indicative equivalents — verify with each destination&apos;s authority.
                        </div>
                      </>
                    ) : null
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default ExportResultsComp;
